import { createFile, parseCsvData } from '@/utils/file'
import { Guid } from '@/utils/guid'
import { getFormatMessage, ipReg } from '@/utils/utils'
import { FileOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Radio,
  Row,
  Space,
  Table,
  Upload
} from 'antd'
import { RcFile } from 'antd/lib/upload'
import React, { Fragment, useEffect, useState } from 'react'
import {
  BoxComMode,
  BoxImportProps,
  BoxImportSearchItem,
  BoxImportType,
  BoxItem
} from '../interface'

export const BoxImport: React.FC<BoxImportProps> = (props: BoxImportProps) => {
  const { importTypes } = props
  const [importType, setImportType] = useState<BoxImportType>(0)
  const [selectFile, setSelectFile] = useState<string>('')
  const [selectItems, setSelectItems] = useState<BoxItem[]>([])
  const [form] = Form.useForm<BoxImportSearchItem>()

  const formatMessage = getFormatMessage()

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 4 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 20 }
    }
  }

  const columns = [0, 1, 2].includes(importType)
    ? [
        {
          title: formatMessage({ id: 'app.box.import.index' }),
          key: 'app.box.import.index',
          dataIndex: 'index',
          render: (text: any, record: any, index: any) => index + 1
        },
        {
          title: formatMessage({ id: 'app.box.import.alias' }),
          key: 'app.box.import.alias',
          dataIndex: 'alias'
        },
        {
          title: formatMessage({ id: 'app.box.import.boxNo' }),
          key: 'app.box.import.boxNo',
          dataIndex: 'boxNo'
        },
        {
          title: formatMessage({ id: 'app.box.import.natIp' }),
          key: 'app.box.import.natIp',
          dataIndex: 'natIp'
        }
      ]
    : [
        {
          title: formatMessage({ id: 'app.box.import.index' }),
          key: 'app.box.import.index',
          dataIndex: 'index',
          render: (text: any, record: any, index: any) => index + 1
        },
        {
          title: formatMessage({ id: 'app.box.import.alias' }),
          key: 'app.box.import.alias',
          dataIndex: 'alias'
        },
        {
          title: formatMessage({ id: 'app.box.import.boxNo' }),
          key: 'app.box.import.boxNo',
          dataIndex: 'boxNo'
        },
        {
          title: formatMessage({ id: 'app.box.import.userPassword' }),
          key: 'app.box.import.userPassword',
          dataIndex: 'userPassword'
        },
        {
          title: formatMessage({ id: 'app.box.import.tags' }),
          key: 'app.box.import.tags',
          dataIndex: 'tags'
        }
      ]

  const parseBoxDate = (csv: string) => {
    const items = parseCsvData<BoxItem>(csv, (obj) => {
      obj.id = Guid.raw()
      obj.connectionState = 0
      obj.templateState = 0
      obj.boxType = 0
      obj.networkType = 0
      obj.alias = !obj.alias ? obj.boxNo : obj.alias
      obj.comMode = 0
    })
    props.onFileSelect?.(items)
    props.onChange?.([])
  }

  const uploadProps = {
    showUploadList: false,
    multiple: false,
    accept: '.csv',
    beforeUpload: async (file: RcFile) => {
      const text = await file.text()
      setSelectFile(file.name)
      parseBoxDate(text)
      return false
    }
  }

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectItems(selectedRows)
      props.onChange?.(selectedRows)
    }
  }

  const handleDownload = () => {
    const content = 'alias,boxNo,hardwareId,password,userPassword,tags'
    createFile('box.csv', content)
  }

  const hanldeTypeChange = (value: BoxImportType) => {
    setImportType(value)
    setSelectFile('')
    form.resetFields()
    form.setFieldsValue({ importType: value })
    props.onTypeChange?.(value)
    props.onChange?.([])
  }

  const handleSearch = () => {
    form.validateFields().then((values: BoxImportSearchItem) => {
      props.onSearch?.(values)
    })
  }

  useEffect(() => {
    if (props.formResult) {
      if (selectItems.length <= 0) {
        message.error('请选择需要导入的设备')
        return
      }

      if (props.onImport) {
        let comMode: BoxComMode = 0
        if (importType === 0) comMode = 1
        if (importType === 1 || importType === 2) comMode = 2
        props.onImport(selectItems, comMode)
      }
    }
  }, [props.formResult])

  useEffect(() => {
    if (props.importTypes && props.importTypes.length > 0) {
      setImportType(props.importTypes[0].value)
      form.setFieldsValue({ importType: props.importTypes[0].value })
    }
  }, [])

  return (
    <div>
      <Form {...formItemLayout} form={form}>
        <Form.Item
          label={formatMessage({ id: 'app.box.import.importType' })}
          name='importType'
        >
          <Radio.Group onChange={(e) => hanldeTypeChange(e.target.value)}>
            <Space>
              {importTypes?.map((x) => (
                <Radio key={`import.type.${x.value}`} value={x.value}>
                  {x.text}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        {/** USB查找 */}
        {importType === 0 && (
          <Form.Item label={formatMessage({ id: 'app.box.import.operation' })}>
            <Button type='primary' onClick={handleSearch}>
              {formatMessage({ id: 'app.box.import.find' })}
            </Button>
          </Form.Item>
        )}

        {/** 广播搜索 */}
        {importType == 1 && (
          <Form.Item label={formatMessage({ id: 'app.box.import.operation' })}>
            <Button type='primary' onClick={handleSearch}>
              {formatMessage({ id: 'app.box.import.search' })}
            </Button>
          </Form.Item>
        )}

        {/** 指定IP导入 */}
        {importType === 2 && (
          <Form.Item
            label={formatMessage({ id: 'app.box.import.ip' })}
            name='ip'
            rules={[
              {
                required: true,
                pattern: new RegExp(ipReg),
                message: formatMessage({
                  id: 'app.box.import.ip.message'
                })
              }
            ]}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Input placeholder='示例: 192.168.1.1' />
              </Col>
              <Col span={12}>
                <Button type='primary' onClick={handleSearch}>
                  {formatMessage({ id: 'app.box.import.search' })}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        )}

        {/** 文件导入 */}
        {importType === 3 && (
          <Form.Item
            label={formatMessage({ id: 'app.box.import.selectFile' })}
            rules={[
              {
                required: true,
                message: formatMessage({
                  id: 'app.box.import.selectFile.message'
                })
              }
            ]}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Input placeholder='' value={selectFile} readOnly />
              </Col>
              <Col span={12}>
                <Space>
                  <Upload {...uploadProps}>
                    <Button type='primary' icon={<FileOutlined />} />
                  </Upload>
                  {!props.templateUrl ? (
                    <Button type='primary' onClick={handleDownload}>
                      {formatMessage({ id: 'app.box.import.downloadTemplate' })}
                    </Button>
                  ) : (
                    <Button type='primary' href={props.templateUrl}>
                      {formatMessage({ id: 'app.box.import.downloadTemplate' })}
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </Form.Item>
        )}

        {/** 标签导入 */}
        {importType === 4 && (
          <Fragment>
            <Form.Item
              label={formatMessage({ id: 'app.box.import.boxNo' })}
              name='boxNo'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.import.boxNo.message'
                  })
                }
              ]}
            >
              <Row gutter={8}>
                <Col span={12}>
                  <Input />
                </Col>
                <Col span={12}></Col>
              </Row>
            </Form.Item>

            <Form.Item
              label={formatMessage({ id: 'app.box.import.userPassword' })}
              name='userPassword'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.box.import.userPassword.message'
                  })
                }
              ]}
            >
              <Row gutter={8}>
                <Col span={12}>
                  <Input />
                </Col>
                <Col span={12}>
                  <Button type='primary' onClick={handleSearch}>
                    {formatMessage({ id: 'app.box.import.add' })}
                  </Button>
                </Col>
              </Row>
            </Form.Item>
          </Fragment>
        )}
      </Form>

      <Table
        rowKey='id'
        rowSelection={{
          type: 'checkbox',
          ...rowSelection
        }}
        columns={columns}
        size='small'
        dataSource={props.data}
      />
    </div>
  )
}
